import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Slide,
    IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { getUserNotifications, markNotificationSeen } from '../api/getClassesApi';
import { clearApplicationBadge } from '../utils/pushNotifications';

const NotificationContainer = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1300,

    maxHeight: '80vh', // Limit to 80% of viewport height
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
        padding: '16px 20%', // Reasonable width on desktop
        maxHeight: '70vh', // Slightly less on desktop
    },
    [theme.breakpoints.up('lg')]: {
        padding: '16px 25%', // Even more reasonable on large screens
        maxHeight: '60vh', // Even less on large screens
    }
}));

const StyledCard = styled(Card)(({ theme }) => ({
    width: '100%',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '0 0 25px 25px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    position: 'relative',
    maxHeight: 'calc(80vh - 32px)', // Account for container padding
    display: 'flex',
    flexDirection: 'column',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
        backgroundSize: '200% 100%',
        animation: 'gradient 3s ease infinite'
    },
    '@keyframes gradient': {
        '0%': {
            backgroundPosition: '0% 50%'
        },
        '50%': {
            backgroundPosition: '100% 50%'
        },
        '100%': {
            backgroundPosition: '0% 50%'
        }
    },
    [theme.breakpoints.up('md')]: {
        maxHeight: 'calc(70vh - 32px)',
    },
    [theme.breakpoints.up('lg')]: {
        maxHeight: 'calc(60vh - 32px)',
    }
}));

const HeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingTop: '8px' // Add some space below the gradient line
}));

const ContentBox = styled(Box)(({ theme }) => ({
    marginBottom: '24px',
    maxHeight: 'none', // Remove max height restriction
    overflowY: 'visible', // Change from auto to visible
    '& p': {
        margin: '0 0 8px 0',
        lineHeight: 1.6,
        color: '#333'
    },
    '& p:empty': {
        margin: '0 0 4px 0',
        height: '4px'
    },
    '& p br:only-child': {
        display: 'block',
        margin: '4px 0',
        content: '""'
    },
    '& p:has(br:only-child)': {
        margin: '0 0 4px 0',
        lineHeight: 1
    },
    '& p + p:empty, & p:empty + p': {
        margin: '0 0 2px 0'
    },
    '& p:last-child': {
        marginBottom: 0
    },
    '& strong': { fontWeight: 600 },
    '& ul, & ol': {
        paddingLeft: '20px',
        margin: '0 0 8px 0'
    },
    '& li': {
        marginBottom: '4px',
        color: '#333'
    },
    '& a': {
        color: '#1976d2',
        textDecoration: 'underline',
        '&:hover': {
            textDecoration: 'none'
        }
    },
    '& h1, & h2, & h3': {
        margin: '0 0 8px 0',
        fontWeight: 600,
        color: '#333'
    },
    '& br': {
        lineHeight: 0.5
    }
}));

const BottomBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
}));

const GotItButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #333 0%, #555 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '12px 28px',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: '100px',
    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #555 0%, #777 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
    },
    '&:active': {
        transform: 'translateY(0)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
    },
    '&:focus': {
        outline: 'none'
    }
}));

const CounterText = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    color: '#888',
    fontWeight: 500,
    backgroundColor: '#f5f5f5',
    padding: '4px 10px',
    borderRadius: '12px',
    border: '1px solid #e8e8e8'
}));

const TitleText = styled(Typography)(({ theme }) => ({
    fontSize: '18px',
    fontWeight: 700,
    color: '#2c3e50',
    margin: 0,
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
}));

const UserNotificationDisplay = ({ onAllNotificationsSeen, preferredNotificationId = null }) => {
    const [notifications, setNotifications] = useState([]);
    const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);
    const [showNotification, setShowNotification] = useState(false);
    const [slideIn, setSlideIn] = useState(false);

    useEffect(() => {
        fetchNotifications();
    }, []);

    useEffect(() => {
        if (notifications.length > 0) {
            setShowNotification(true);
            // Small delay for smooth animation
            setTimeout(() => setSlideIn(true), 100);
        }
    }, [notifications]);

    const fetchNotifications = async () => {
        try {
            const data = await getUserNotifications();
            if (data && data.length > 0) {
                if (preferredNotificationId) {
                    const prioritized = [...data].sort((a, b) => {
                        if (a.id === preferredNotificationId) return -1;
                        if (b.id === preferredNotificationId) return 1;
                        return 0;
                    });
                    setNotifications(prioritized);
                } else {
                    setNotifications(data);
                }
            } else {
                clearApplicationBadge();
                // No notifications, proceed to classes
                onAllNotificationsSeen();
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            // On error, proceed to classes
            onAllNotificationsSeen();
        }
    };

    const handleGotIt = async () => {
        const currentNotification = notifications[currentNotificationIndex];

        try {
            // Mark current notification as seen
            await markNotificationSeen(currentNotification.id);

            // Slide out animation
            setSlideIn(false);

            setTimeout(() => {
                if (currentNotificationIndex < notifications.length - 1) {
                    // Show next notification
                    setCurrentNotificationIndex(prev => prev + 1);
                    setSlideIn(true);
                } else {
                    // All notifications seen, proceed to classes
                    setShowNotification(false);
                    clearApplicationBadge();
                    onAllNotificationsSeen();
                }
            }, 300);

        } catch (error) {
            console.error('Error marking notification as seen:', error);
            // On error, still proceed
            setShowNotification(false);
            onAllNotificationsSeen();
        }
    };

    const handleClose = () => {
        setSlideIn(false);
        setTimeout(() => {
            setShowNotification(false);
            clearApplicationBadge();
            onAllNotificationsSeen();
        }, 300);
    };

    const createMarkup = (html) => {
        return { __dangerouslySetInnerHTML: { __html: html } };
    };

    if (!showNotification || notifications.length === 0) {
        return null;
    }

    const currentNotification = notifications[currentNotificationIndex];

    // Safety check - if current notification is undefined, don't render
    if (!currentNotification) {
        return null;
    }

    return (
        <NotificationContainer>
            <Slide direction="down" in={slideIn} timeout={500}>
                <StyledCard>
                    <CardContent sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        overflow: 'hidden'
                    }}>
                        <HeaderBox>
                            <TitleText variant="h6">
                                {currentNotification.title}
                            </TitleText>
                            <IconButton
                                size="small"
                                onClick={handleClose}
                                sx={{
                                    color: '#666',
                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </HeaderBox>

                        <Box sx={{
                            flex: 1,
                            overflowY: 'auto',
                            marginBottom: '24px',
                            minHeight: 0, // Important for flex overflow
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#ccc',
                                borderRadius: '3px',
                                '&:hover': {
                                    background: '#999',
                                }
                            }
                        }}>
                            <ContentBox>
                                <div dangerouslySetInnerHTML={{ __html: currentNotification.content }} />
                            </ContentBox>
                        </Box>

                        <BottomBox>
                            <CounterText>
                                {currentNotificationIndex + 1} of {notifications.length}
                            </CounterText>
                            <GotItButton onClick={handleGotIt}>
                                Got it!
                            </GotItButton>
                        </BottomBox>
                    </CardContent>
                </StyledCard>
            </Slide>
        </NotificationContainer>
    );
};

export default UserNotificationDisplay;